import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Link,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { LogoutForm } from '@/components/authentication/LogoutForm';
import { OrganizationAutocomplete } from '@/components/form-elements/OrganizationAutocomplete';
import { I18N_DE } from '@/i18n/de';
import { EMAIL_PATTERN } from '@/modules/EMAIL_PATTERN';
import { PHONE_NUMBER_PATTERN } from '@/modules/PHONE_NUMBER_PATTERN';
import { CreateUserDto, Organization, SingleSignOnUserDto, UserInstitutionType } from '@/modules/api';
import { backendApiUsersCreateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useUserQuery } from '@/queries/useUserQuery';
import { getApiValidationError } from '@/utils/ErrorHelper';

type UserRegistrationFormProps = { singleSignOnUser: SingleSignOnUserDto };

export function UserRegistrationForm({ singleSignOnUser }: UserRegistrationFormProps) {
  const { data: user } = useUserQuery();

  const formId = useId();

  const { mutate, error: mutateError, isPending } = useMutation(backendApiUsersCreateMutation());
  const validationError = getApiValidationError(mutateError);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Omit<CreateUserDto, 'organizationId'> & { organization: Organization; termsAccepted: boolean }>();

  useEffect(() => {
    if (user) reset({ email: user?.email });
  }, [reset, user]);

  return (
    singleSignOnUser && (
      <>
        <Stack
          direction="column"
          gap={4}
          component="form"
          id={formId}
          onSubmit={handleSubmit(({ organization, termsAccepted, ...data }) => {
            if (isPending) return;
            mutate({ body: { ...data, organizationId: organization?.id } });
          })}
        >
          <Typography level="h1" textAlign="center">
            Registrierung
          </Typography>

          <Alert color="warning">
            <Typography>
              Das Verkehrszeichenkataster ist für Institutionen innerhalb der Verwaltung konzipiert. Bei Interesse an
              Verkehrszeichen-Daten empfehlen wir die Plattform{' '}
              <Link href="https://mobidata-bw.de/dataset/verkehrszeichen-baden-wurttemberg">MobiData BW</Link>.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid xs={6}>
              <FormControl error={!!errors.email}>
                <FormLabel>Dienstliche E-Mail-Adresse</FormLabel>
                <Input
                  variant="outlined"
                  {...register('email', { required: true, pattern: EMAIL_PATTERN })}
                  placeholder="E-Mail"
                  type="email"
                />
                {errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={6}>
              <FormControl error={!!errors.phoneNumber}>
                <FormLabel>Telefonnummer</FormLabel>
                <Input
                  variant="outlined"
                  {...register('phoneNumber', { required: true, pattern: PHONE_NUMBER_PATTERN })}
                  type="tel"
                  placeholder="+4915112345678"
                />
                {errors.phoneNumber && <FormHelperText>{errors.phoneNumber.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={6}>
              <FormControl error={!!errors.firstName}>
                <FormLabel>Vorname</FormLabel>
                <Input variant="outlined" {...register('firstName', { required: true, minLength: 2 })} />
                {errors.firstName && <FormHelperText>Pflichtfeld</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={6}>
              <FormControl error={!!errors.lastName}>
                <FormLabel>Nachname</FormLabel>
                <Input variant="outlined" {...register('lastName', { required: true, minLength: 2 })} />
                {errors.lastName && <FormHelperText>Pflichtfeld</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={6}>
              <FormControl error={!!errors.institutionType}>
                <FormLabel>Institution</FormLabel>
                <Controller
                  control={control}
                  name="institutionType"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field} onChange={(event, value) => field.onChange(value)} variant="outlined">
                      {Object.values(UserInstitutionType).map((option) => (
                        <Option key={option} value={option}>
                          {I18N_DE.UserInstitutionType[option]}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                {errors.institutionType && <FormHelperText>Pflichtfeld</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={6}>
              <FormControl error={!!errors.roleText}>
                <FormLabel>Funktion</FormLabel>
                <Input variant="outlined" {...register('roleText', { required: true, minLength: 1 })} />
                {errors.roleText && <FormHelperText>Pflichtfeld</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl error={!!errors.organization}>
                <FormLabel>Organisationen</FormLabel>
                <Controller
                  name="organization"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <OrganizationAutocomplete
                      variant="outlined"
                      value={value}
                      onChange={(event, newValue) => {
                        onChange(newValue);
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid xs={12} sx={{ mb: 2, mt: 2 }}>
              <FormControl error={!!errors.termsAccepted}>
                <Controller
                  control={control}
                  name="termsAccepted"
                  defaultValue={false}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Checkbox
                      onChange={field.onChange}
                      checked={field.value}
                      label="Ich habe die Nutzungsbedingungen und die Datenschutzerklärung gelesen und akzeptiere sie."
                    />
                  )}
                />
                {errors.termsAccepted && <FormHelperText>Pflichtfeld</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} sx={{ mb: 2 }}>
              <Typography level="body-lg">
                Bei Fragen wenden Sie sich gerne an{' '}
                <Link href="mailto:verkehrszeichenkataster@vm.bwl.de">verkehrszeichenkataster@vm.bwl.de</Link>.
              </Typography>
            </Grid>
            <Grid xs={6}>
              <LogoutForm flex="0 0 0">
                <Button type="submit" variant="outlined" fullWidth>
                  Logout
                </Button>
              </LogoutForm>
            </Grid>
            <Grid xs={6}>
              <Button fullWidth type="submit" form={formId} loading={isPending} disabled={isPending}>
                Registrieren
              </Button>
            </Grid>
          </Grid>
        </Stack>

        {mutateError && (
          <Alert style={{ marginTop: 10 }} color="danger">
            {`Registrierung fehlgeschlagen. ${validationError?.message ?? ''}`}
          </Alert>
        )}
      </>
    )
  );
}
